<script setup lang="ts">
import { attachmentUrl } from "../composables/useMessaging"
import { t } from "../lib/i18n"
import type { Attachment } from "../lib/localdb"

// A message's files: pictures as thumbnails (opened large over the conversation), other files as
// chips that open the file. Addresses are short-lived links, fetched when shown.
const props = defineProps<{ files: Attachment[]; sending?: boolean }>()

/** Pictures a browser shows (not HEIC) */
const SHOWN = /^image\/(jpeg|png|gif|webp)$/
const pictures = computed(() => props.files.filter((f) => SHOWN.test(f.contentType)))
const others = computed(() => props.files.filter((f) => !SHOWN.test(f.contentType)))
const urls = reactive<Record<string, string>>({})
const failed = reactive<Record<string, boolean>>({})
const large = ref<Attachment | null>(null)

async function load(file: Attachment) {
  if (urls[file.id] || failed[file.id]) return
  try {
    urls[file.id] = await attachmentUrl(file.id)
  } catch {
    // Not on the server yet (still sending) or offline: the chip stays
    failed[file.id] = true
  }
}

watch(
  () => [pictures.value.map((f) => f.id).join(), props.sending],
  () => {
    for (const f of pictures.value) {
      // Sent now: the server has it, so a link that failed while sending works
      if (!props.sending && failed[f.id] && !urls[f.id]) delete failed[f.id]
      void load(f)
    }
  },
  { immediate: true },
)

async function open(file: Attachment) {
  if (SHOWN.test(file.contentType)) {
    large.value = file
    return
  }
  // A tab before the link arrives: browsers allow it only right after the tap
  const tab = window.open("", "_blank")
  try {
    const url = await attachmentUrl(file.id)
    if (tab) {
      tab.opener = null
      tab.location.href = url
    } else window.open(url, "_blank")
  } catch {
    tab?.close()
  }
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>

<template>
  <div class="files">
    <div v-if="pictures.length" class="pictures" :class="{ several: pictures.length > 1 }">
      <button v-for="f in pictures" :key="f.id" type="button" class="picture" :aria-label="f.name" @click="open(f)">
        <img v-if="urls[f.id]" :src="urls[f.id]" :alt="f.name" loading="lazy" @error="failed[f.id] = true" />
        <HubIcon v-else name="file" :size="28" />
      </button>
    </div>
    <button v-for="f in others" :key="f.id" type="button" class="chip" :disabled="sending" @click="open(f)">
      <HubIcon name="file" :size="22" />
      <span class="name">{{ f.name }}</span>
      <span class="size">{{ size(f.size) }}</span>
    </button>

    <Teleport to="body">
      <div v-if="large" class="viewer" role="dialog" :aria-label="large.name" @click.self="large = null">
        <img :src="urls[large.id]" :alt="large.name" />
        <div class="viewer-bar">
          <span>{{ large.name }}</span>
          <a v-if="urls[large.id]" class="hub-icon-button" :href="urls[large.id]" target="_blank" rel="noopener" :aria-label="t('Download')">
            <HubIcon name="download" :size="22" />
          </a>
          <button class="hub-icon-button" type="button" :aria-label="t('Close')" @click="large = null">
            <HubIcon name="close" :size="22" />
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.files {
  display: grid;
  gap: 6px;
  margin: 2px 0 4px;
}

.pictures {
  display: grid;
  gap: 4px;
}

.pictures.several {
  grid-template-columns: 1fr 1fr;
}

/* A fixed box: the conversation doesn't move when the picture arrives */
.picture {
  display: grid;
  grid-template: 100% / 100%;
  place-items: center;
  width: 240px;
  max-width: 100%;
  height: 180px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: var(--hub-tint);
  color: var(--hub-muted);
  cursor: zoom-in;
}

.picture img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.several .picture {
  width: 118px;
  height: 118px;
}

.chip {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--hub-border);
  border-radius: 10px;
  background: var(--hub-surface);
  color: var(--hub-text);
  text-align: left;
  font: inherit;
}

.chip .name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 0.9rem;
}

.chip .size {
  color: var(--hub-muted);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.viewer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  background: rgb(0 0 0 / 0.9);
  padding: max(56px, env(safe-area-inset-top)) 8px max(16px, env(safe-area-inset-bottom));
}

.viewer img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.viewer-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: max(8px, env(safe-area-inset-top)) 8px 8px 16px;
  color: #fff;
}

.viewer-bar span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-bar .hub-icon-button {
  color: #fff;
}

.hub-theme-pos .picture,
.hub-theme-pos .chip {
  border-radius: 2px;
}
</style>
